import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { HostedForm } from '../../../base/interfaces/form';
import { FluidpayFieldStyles } from './fluidpay-tokenizer.styles';
@Component({
    selector: 'app-fluidpay-tokenizer',
    templateUrl: './fluidpay-tokenizer.component.html',
    styleUrls: ['./fluidpay-tokenizer.component.css']
})

/*
Bouncing Ball when user clicks submit button on V2 page and this FluidPay Tokenizer Hosted Form is being displayed:
    1) Submit Clicked and submitForm() on form.component gets run.
    2) Validate regular fields (that we display).
    3) Call this.hostedFormComponent.tokenize() (when there is a hosted form).
    4) tokenize() function calls the this.tokenizer.submit() WHERE tokenizer is the instance of the Tokenizer logic returned by FluidPay.
    5) When this.tokenizer.submit() is called, it calls the FluidPay Tokenizer submit, which does whatever logic they do: Check Accounting and Routing numbers are accurate and gives us FluidpayFieldStylesa single-use-token to use to call the CreateACH endpoint (this is done on backend).
    6) Once the FluidPay logic is done, the code hits the submission logic above (initialized in the new Tokenizer logic in the FluidpayTokenizerComponent).
    7) The submission logic will call the .resolve() with the necessary output (An associative array with keys success, errorMessage (if it failed), or token (if it passed).
    8) The token is then passed to the BE OnSubmit (through the FormData).
    9) The BE OnSubmit then calls the CreateACH endpoint with the Single Use Token generated here in the FE.
*/

export class FluidpayTokenizerComponent
    implements AfterViewInit, OnChanges, OnDestroy {

    // Given a value in easysuite-form.component.html, using the selector value
    @Input() hostedForm: HostedForm;

    private static nextID = 0;

    readonly containerID = `fluidpay-tokenizer-${++FluidpayTokenizerComponent.nextID}`;

    fluidPayFormFinishedLoading = false;
    errorMessage = '';

    // Instance created by Fluidpay's Tokenizer constructor
    private tokenizer: any;

    private viewReady = false;
    private destroyed = false;

    // Used to ignore callbacks from an old tokenizer instance.
    private generation = 0;

    // Stores the function that returns the tokenization result to submitForm()
    private PendingSubmissionReturnResult?: {
        resolve: (result: {
            success: 1 | 0;
            token: string;
            errorMessage: string;
        }) => void;
    };

    constructor(
        private zone: NgZone
    ) {}

    ngAfterViewInit(): void {
        this.viewReady = true;

        // Wait until Angular finishes rendering the container.
        Promise.resolve().then(() => this.initialize());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hostedForm'] && this.viewReady) {
            Promise.resolve().then(() => this.initialize());
        }
    }

    // Load Fluidpay's SDK and create the hosted form inside our container
    private async initialize(): Promise<void> {

        if (this.destroyed) return;

        this.errorMessage = '';

        const generation = this.generation;

        try {

            // Read the settings originally returned by BE OnLoad.
            const hostedForm = this.hostedForm;
            const params = hostedForm.Params;

            const publicKey = params?.['PublicKey'];
            const paymentType = params?.['PaymentType'];
            const production = params?.['Production'];
            var urlEndpoint = "";

            // Get the prod or test url endpoint
            if (production == 1) {
                urlEndpoint = "https://app.fluidpay.com";
            }
            else {
                urlEndpoint = "https://sandbox.fluidpay.com";
            }

            // Wait until Fluidpay's script has loaded and we can use its constructor.
            const Tokenizer = await this.loadFluidPayTokenizer(urlEndpoint);

            // The user could have left the page while the script was loading.
            // This is to stop the Fluidpay Iframe from loading.
            if (!this.isCurrent(generation)) return;

            // Create Fluidpay's hosted form and register its callbacks.
            this.tokenizer = new Tokenizer({
                url: urlEndpoint,
                apikey: publicKey,
                container: `#${this.containerID}`,
                settings: {
                    payment: {
                        types: [paymentType]
                    },
                    // Styles applied to elements within the FluidPay Form.
                    styles: FluidpayFieldStyles
                },

                // Runs when the hosted form finishes loading.
                // fluidPayFormFinishedLoading is used in html to not display Loading bank account fields... text
                onLoad: () => {
                    this.zone.run(() => {
                        if (!this.isCurrent(generation)) return;
                        this.fluidPayFormFinishedLoading = true;
                    });
                },

                // Runs when this.tokenizer.submit() is called after FluidPay does their tokenizer logic.
                // This is our evaluation of the response returned from FluidPay Tokenizer.
                // PATH: Submit Form -> tokenize() -> tokenizer.submit() -> Calls FluidPay endpoint to see if bank account can be added -> submission() 
                submission: (response: any) => {

                    // NOTE: response variable is returned by FluidPay Tokenizer endpoint,
                    // and will either return a success with the token
                    // or return an error message

                    this.zone.run(() => {

                        // NOTE: Calling the .resolved() outputs the result in this.tokenizer.submit() function.

                        // Return a successful result to the awaiting submitForm().
                        if (
                            response?.status === 'success' &&
                            typeof response.token === 'string' &&
                            response.token.trim() !== ''
                        ) {

                            this.PendingSubmissionReturnResult.resolve({
                                success: 1,
                                token: response.token,
                                errorMessage: ''
                            });
                            return;
                        }
                        // The response was an error
                        else {

                            let errorMessage = "Error when proccessing ACH: ";

                            // Field Validation Failed
                            if (response.status == "validation") {

                                /*
                                List out invalid field labels in error message.
                                Format the response.invalid to be more user friendly. The following are the three values it could be based on what fields are invalid:
                                    - "account"
                                    - "routing"
                                    - "account,routing"
                                We would like it to say "Account Number", "Routing Number", or "Account Number and Routing Number"
                                */
                                let invalidFieldsText = '';

                                if (response.invalid == 'account') {
                                    invalidFieldsText = 'Account Number';
                                }
                                else if (response.invalid == 'routing') {
                                    invalidFieldsText = 'Routing Number';
                                }
                                else if (response.invalid == 'account,routing') {
                                    invalidFieldsText = 'Account Number and Routing Number';
                                }

                                errorMessage += "Invalid Field(s) : " + invalidFieldsText + ". Please correct the bank account details and submit again.";
                            }
                            // More generic error occured (network, invalid API key, etc)
                            else {

                                // Show the error messsage that Fluidpay returned
                                errorMessage += response.msg;
                            }

                            // Return an unsuccessful result to the awaiting submitForm().
                            this.PendingSubmissionReturnResult.resolve({
                                success: 0,
                                token: '',
                                errorMessage: errorMessage
                            });
                        }

                        
                    });
                }

            });
        } catch (error) {
            if (!this.isCurrent(generation)) return;

            this.errorMessage = error instanceof Error
                ? error.message
                : 'Unable to initialize the bank account form.';
        }
    }

    // Add Fluidpay's script to the page and return its Tokenizer constructor.
    private loadFluidPayTokenizer(urlEndpointBase): Promise<any> {

        const scriptPromise = new Promise<any>((resolve) => {
            
            // Create a script element, but do not add it to the page yet.
            const script = document.createElement('script');

            script.src = `${urlEndpointBase}/tokenizer/tokenizer.js`;
            
            // The browser calls this after the script loads and executes.
            script.onload = () => {

                // Get the Tokenizer constructor that fluidpay returns.
                const Tokenizer = (window as any).Tokenizer;
                resolve(Tokenizer);
            };
            document.head.appendChild(script);
        });

        return scriptPromise;
    }

    // Call Fluidpay to tokenize the bank details.
    // The submission callback completes this Promise with the result.
    tokenize(): Promise<{success: 1 | 0; token: string; errorMessage: string;}> {

        return new Promise(resolve => {

            // Save this Promise's resolve function so the submission callback
            // can complete it later with the tokenization result.
            // When resolve is called, either within the submission above OR in catch below if there is an error,
            // That value will be returned as the Promise to where tokenize() is called.
            this.PendingSubmissionReturnResult = { resolve };

            try {
                // this calls the submission functionality instantiated inside of the initialize.
                this.tokenizer.submit();
            } catch {
                this.PendingSubmissionReturnResult = undefined;

                resolve({
                    success: 0,
                    token: '',
                    errorMessage: 'Bank account verification could not start.'
                });
            }
        });
    }


    private isCurrent(generation: number): boolean {
        return !this.destroyed && generation === this.generation;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }
}